import { useState, useEffect, useMemo } from 'react';
import { Search, Loader2, Download, X } from 'lucide-react';
import { apiService } from '../config/api';
import toast from 'react-hot-toast';
import CSVExportModal from '../components/CSVExportModal';
import SmartPagination, { PAGE_SIZE_OPTIONS } from '../components/SmartPagination';

const DEFAULT_LEADS_PAGE_SIZE = 50;

function LeadSkeletonRows({ count = 10 }) {
    return Array.from({ length: count }).map((_, i) => (
        <tr key={i} className="border-b border-slate-100 dark:border-slate-800">
            <td className="px-6 py-4"><div className="h-4 w-6 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" /></td>
            <td className="px-6 py-4"><div className="h-4 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" style={{ width: `${130 + (i % 5) * 25}px` }} /></td>
            <td className="px-6 py-4"><div className="h-4 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" style={{ width: '120px' }} /></td>
            <td className="px-6 py-4"><div className="h-5 w-16 bg-slate-200 dark:bg-slate-700 rounded-full animate-pulse" /></td>
            <td className="px-6 py-4"><div className="h-4 w-10 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" /></td>
            <td className="px-6 py-4"><div className="h-4 w-20 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" /></td>
            <td className="px-6 py-4"><div className="h-4 w-14 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" /></td>
            <td className="px-6 py-4"><div className="h-5 w-12 bg-slate-200 dark:bg-slate-700 rounded-full animate-pulse" /></td>
        </tr>
    ));
}

const ALL_STATUSES = [
    'Valid',
    'Invalid',
    'Catch All',
    'Unknown',
    'Disposable',
    'Role Account',
    'Free Account',
    'Skipped',
    'Syntax Error',
];

function getStatusBadgeClass(status) {
    switch (status) {
        case 'Valid': return 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800';
        case 'Invalid': return 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800';
        case 'Catch All': return 'bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-300 dark:border-yellow-800';
        case 'Disposable': return 'bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900/30 dark:text-orange-300';
        case 'Unknown': return 'bg-purple-100 text-purple-800 border-purple-200 dark:bg-purple-900/30 dark:text-purple-300';
        case 'Role Account': return 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300';
        case 'Free Account': return 'bg-sky-100 text-sky-800 border-sky-200 dark:bg-sky-900/30 dark:text-sky-300';
        case 'Skipped': return 'bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-400';
        case 'Syntax Error': return 'bg-pink-100 text-pink-800 border-pink-200 dark:bg-pink-900/30 dark:text-pink-300';
        default: return 'bg-gray-100 text-gray-600 border-gray-200 dark:bg-gray-800 dark:text-gray-400';
    }
}

function getSafetyLabel(status) {
    if (status === 'Valid') return { label: 'Safe', cls: 'text-green-600 dark:text-green-400' };
    if (status === 'Catch All') return { label: 'Risky', cls: 'text-yellow-600 dark:text-yellow-400' };
    return { label: 'Unsafe', cls: 'text-red-500 dark:text-red-400' };
}

export default function LeadManagement() {
    const [emails, setEmails] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [tagFilter, setTagFilter] = useState('');
    const [nodeFilter, setNodeFilter] = useState('');
    const [groupFilter, setGroupFilter] = useState('');
    const [availableNodes, setAvailableNodes] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [availableGroups, setAvailableGroups] = useState([]);
    const [exportModalOpen, setExportModalOpen] = useState(false);
    const [pageSize, setPageSize] = useState(() =>
        Number(sessionStorage.getItem('leadsPageSize')) || DEFAULT_LEADS_PAGE_SIZE
    );

    const [debouncedSearch, setDebouncedSearch] = useState('');
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search.trim()), 400);
        return () => clearTimeout(t);
    }, [search]);

    useEffect(() => {
        fetchEmails();
    }, [currentPage, pageSize, statusFilter, nodeFilter, groupFilter, debouncedSearch]);

    useEffect(() => {
        fetchAvailableGroups();
    }, []);

    useEffect(() => {
        setCurrentPage(1);
    }, [statusFilter, nodeFilter, groupFilter, debouncedSearch]);

    const fetchEmails = async () => {
        setLoading(true);
        try {
            const params = {
                limit: pageSize,
                offset: (currentPage - 1) * pageSize,
                status: statusFilter || undefined,
                search: debouncedSearch || undefined,
                tag: tagFilter || undefined,
                node: nodeFilter || undefined,
                group: groupFilter || undefined,
            };
            const res = await apiService.getLeads(params);
            const items = res.data.data.emails || res.data.data.items || [];
            setEmails(items);
            setTotal(res.data.data.total || 0);
            // Collect unique node names for the dropdown
            const nodes = [...new Set(items.map(e => e.satellite_node_name).filter(Boolean))];
            setAvailableNodes(prev => [...new Set([...prev, ...nodes])]);
        } catch (err) { } finally {
            setLoading(false);
        }
    };

    // Derive extra column keys from the loaded page (Apollo-style dynamic columns)
    const extraColumnKeys = useMemo(() => {
        const keySet = new Set();
        for (const r of emails) {
            if (!r.extra_columns) continue;
            try {
                const extra = typeof r.extra_columns === 'string'
                    ? JSON.parse(r.extra_columns)
                    : r.extra_columns;
                if (extra && typeof extra === 'object' && !Array.isArray(extra)) {
                    Object.keys(extra).forEach(k => {
                        if (k !== 'tags' && k !== 'email') keySet.add(k);
                    });
                }
            } catch (_) {}
        }
        return [...keySet];
    }, [emails]);

    const fetchAvailableGroups = async () => {
        try {
            const response = await apiService.getGroups();
            const groups = response.data.data || [];
            setAvailableGroups(groups);
        } catch (error) {
            console.error("Failed to load available groups:", error);
        }
    };

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        setDebouncedSearch(search.trim());
        setCurrentPage(1);
    };

    const clearFilters = () => {
        setSearch('');
        setStatusFilter('');
        setNodeFilter('');
        setGroupFilter('');
        setCurrentPage(1);
    };

    const totalPages = Math.max(1, Math.ceil(total / pageSize) || 1);

    const handlePageSizeChange = (n) => {
        if (!PAGE_SIZE_OPTIONS.includes(n)) return;
        sessionStorage.setItem('leadsPageSize', String(n));
        setPageSize(n);
        setCurrentPage(1);
    };

    return (
        <div className="space-y-6">
            <CSVExportModal
                open={exportModalOpen}
                onClose={() => setExportModalOpen(false)}
                selectedJob={{ job_id: 'ALL', job_name: 'All Leads' }}
                selectedFilters={{ status: statusFilter, search, tag: tagFilter, group: groupFilter }}
            />

            <div className="flex justify-between items-center bg-white dark:bg-slate-900 p-6 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-800">
                <div>
                    <h1 className="text-2xl font-bold">Lead Management</h1>
                    <p className="text-slate-500 text-sm">Organize and search your global lead database.</p>
                </div>
                <button className="btn btn-primary" onClick={() => setExportModalOpen(true)}>
                    <Download className="h-4 w-4 mr-2" />Export All
                </button>
            </div>

            <div className="card">
                {/* Filters */}
                <div className="card-header bg-slate-50/50 dark:bg-slate-900/50 flex flex-col md:flex-row gap-3 flex-wrap">
                    {/* Search */}
                    <form onSubmit={handleSearchSubmit} className="relative flex-1 min-w-[180px]">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <input
                            className="input pl-10 h-10 w-full"
                            placeholder="Search emails..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                    </form>

                    {/* Group Filter */}
                    {availableGroups.length > 0 && (
                        <select
                            className="input h-10 py-0 w-full md:w-44"
                            value={groupFilter}
                            onChange={e => setGroupFilter(e.target.value)}
                        >
                            <option value="">All Groups</option>
                            {availableGroups.map(g => (
                                <option key={g.name || g} value={g.name || g}>🏷️ {g.name || g} ({g.count || '?'})</option>
                            ))}
                        </select>
                    )}

                    {/* Status filter — all statuses */}
                    <select
                        className="input h-10 py-0 w-full md:w-48"
                        value={statusFilter}
                        onChange={e => setStatusFilter(e.target.value)}
                    >
                        <option value="">All Statuses</option>
                        {ALL_STATUSES.map(s => (
                            <option key={s} value={s}>{s}</option>
                        ))}
                    </select>

                    {/* Node filter */}
                    {availableNodes.length > 0 && (
                        <select
                            className="input h-10 py-0 w-full md:w-40"
                            value={nodeFilter}
                            onChange={e => setNodeFilter(e.target.value)}
                        >
                            <option value="">All Nodes</option>
                            {availableNodes.map(n => (
                                <option key={n} value={n}>{n}</option>
                            ))}
                        </select>
                    )}

                    {/* Clear */}
                    <button className="btn btn-secondary h-10 px-4 shrink-0" onClick={clearFilters}>
                        <X className="h-4 w-4 mr-1" />Clear
                    </button>
                </div>

                <div className="overflow-x-auto relative">
                    {/* Top loading bar */}
                    {loading && (
                        <div className="absolute top-0 left-0 right-0 h-0.5 bg-indigo-100 dark:bg-indigo-900/40 overflow-hidden z-10">
                            <div className="h-full bg-indigo-500 animate-[shimmer_1.5s_ease-in-out_infinite]" style={{ width: '40%', animation: 'leadbar 1.4s ease-in-out infinite' }} />
                        </div>
                    )}
                    <style>{`
                        @keyframes leadbar {
                            0% { transform: translateX(-100%); }
                            100% { transform: translateX(350%); }
                        }
                    `}</style>
                    <table className="w-full">
                        <thead>
                            <tr className="bg-slate-50 dark:bg-slate-800/50 text-left border-b border-slate-100 dark:border-slate-800">
                                <th className="px-6 py-4 text-xs font-bold uppercase">#</th>
                                <th className="px-6 py-4 text-xs font-bold uppercase">Email</th>
                                <th className="px-6 py-4 text-xs font-bold uppercase">List / Group</th>
                                <th className="px-6 py-4 text-xs font-bold uppercase">Status</th>
                                <th className="px-6 py-4 text-xs font-bold uppercase">Safety</th>
                                <th className="px-6 py-4 text-xs font-bold uppercase">Type</th>
                                <th className="px-6 py-4 text-xs font-bold uppercase">Node</th>
                                {extraColumnKeys.map(key => (
                                    <th key={key} className="px-6 py-4 text-xs font-bold uppercase whitespace-nowrap">
                                        {key.replace(/_/g, ' ')}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                            {loading ? (
                                <LeadSkeletonRows count={pageSize} />
                            ) : emails.length === 0 ? (
                                <tr><td colSpan={7 + extraColumnKeys.length} className="p-20 text-center text-slate-400">No leads found.</td></tr>
                            ) : emails.map((email, i) => {
                                const safety = getSafetyLabel(email.status);
                                // Parse extra_columns for dynamic columns
                                return (
                                    <tr key={i} className="hover:bg-slate-50 dark:hover:bg-slate-800/20 transition-colors">
                                        <td className="px-6 py-4 text-sm text-slate-400">{(currentPage - 1) * pageSize + i + 1}</td>
                                        <td className="px-6 py-4 text-sm font-semibold">{email.email}</td>
                                        <td className="px-6 py-4 text-sm">
                                            <div className="flex flex-col gap-1">
                                                <span className="font-medium text-slate-700 dark:text-slate-200 truncate max-w-[180px]" title={email.list_name || 'N/A'}>
                                                    📁 {email.list_name || 'Individual Upload'}
                                                </span>
                                                {email.list_group_name && (
                                                    <span className="inline-flex items-center gap-1 text-[10px] font-bold text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/30 px-1.5 py-0.5 rounded border border-blue-200 dark:border-blue-800 w-fit">
                                                        🏷️ {email.list_group_name}
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase border ${getStatusBadgeClass(email.status)}`}>
                                                {email.status}
                                            </span>
                                        </td>
                                        <td className={`px-6 py-4 text-sm font-medium ${safety.cls}`}>{safety.label}</td>
                                        <td className="px-6 py-4 text-sm text-slate-500">{email.type || 'Standard'}</td>
                                        <td className="px-6 py-4 text-sm text-slate-500">{email.satellite_node_name || 'Master'}</td>
                                        {extraColumnKeys.map(key => {
                                            let extraData = {};
                                            if (email.extra_columns) {
                                                try {
                                                    extraData = typeof email.extra_columns === 'string'
                                                        ? JSON.parse(email.extra_columns)
                                                        : email.extra_columns;
                                                    if (typeof extraData !== 'object' || Array.isArray(extraData)) extraData = {};
                                                } catch (_) {}
                                            }
                                            return (
                                                <td key={key} className="px-6 py-4 text-sm text-slate-600 dark:text-slate-300 max-w-[180px] truncate" title={extraData[key] ?? ''}>
                                                    {extraData[key] !== undefined && extraData[key] !== null && extraData[key] !== ''
                                                        ? String(extraData[key])
                                                        : <span className="text-slate-300 dark:text-slate-600">—</span>
                                                    }
                                                </td>
                                            );
                                        })}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                <div className={`px-6 pb-6 transition-opacity duration-200 ${loading ? 'opacity-50 pointer-events-none' : 'opacity-100'}`}>
                    <SmartPagination
                        currentPage={Math.min(currentPage, totalPages)}
                        totalPages={totalPages}
                        totalRows={total}
                        pageSize={pageSize}
                        onPageChange={setCurrentPage}
                        onPageSizeChange={handlePageSizeChange}
                        disabled={loading}
                    />
                </div>
            </div>
        </div>
    );
}
